import { useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import intl from 'react-intl-universal';
import { useAppThunkDispatch } from 'state/store';
import { selectNumberOfSelectedTasks } from 'state/ProjectTasksList/export/exportSlice';
import {
  selectEditableTasksCount,
  selectTasksMetricsStatus,
  fetchTasksMetrics as fetchProjectTasksMetrics,
  resetTasksMetrics as resetProjectTasksMetrics,
} from 'state/ProjectTasksList/tasksMetrics/taskMetricsSlice';
import { SLICE_STATUS } from 'utils/constants';
import { selectIsAssigneesListVisible } from 'state/ProjectTasksList/bulkActions/bulkAssign/availableAssignees/availableAssigneesSlice';

interface UseTasksMetricsProps {
  isBulkActionsBarVisible?: boolean;
}

export const useTasksMetrics = ({
  isBulkActionsBarVisible,
}: UseTasksMetricsProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useAppThunkDispatch();
  const numberOfSelectedTasks = useSelector(selectNumberOfSelectedTasks);
  const editableTasksCount = useSelector(selectEditableTasksCount);
  const isAssigneesListVisible = useSelector(selectIsAssigneesListVisible);
  const tasksMetricsStatus = useSelector(selectTasksMetricsStatus);
  const isLoadingTasksMetrics = tasksMetricsStatus === SLICE_STATUS.LOADING;

  const fetchTasksMetrics = useCallback(async () => {
    await dispatch(fetchProjectTasksMetrics({ projectId }));
  }, [dispatch, projectId]);

  const editableTasksCountDoesntMatchNumberOfSelectedTasks =
    useMemo<boolean>(() => {
      return editableTasksCount !== undefined
        ? editableTasksCount > 0 && editableTasksCount !== numberOfSelectedTasks
        : false;
    }, [editableTasksCount, numberOfSelectedTasks]);

  const numberOrRowsSelectedLabel = useMemo(() => {
    if (isLoadingTasksMetrics) {
      return '';
    }
    if (
      editableTasksCountDoesntMatchNumberOfSelectedTasks &&
      isAssigneesListVisible
    ) {
      return intl.get('TASKS.BULK_ACTIONS.EDITABLE_TASKS_COUNT', {
        editableTasksCount,
        totalTasksCount: numberOfSelectedTasks,
      });
    }
    return intl.get('TASKS.BULK_ACTIONS.ROWS_SELECTED', {
      num: numberOfSelectedTasks,
    });
  }, [
    editableTasksCountDoesntMatchNumberOfSelectedTasks,
    isLoadingTasksMetrics,
    editableTasksCount,
    numberOfSelectedTasks,
    isAssigneesListVisible,
  ]);

  const resetTasksMetrics = useCallback(() => {
    dispatch(resetProjectTasksMetrics());
  }, [dispatch]);

  const displayEditableTasksCountTooltip = useMemo(() => {
    return (
      editableTasksCountDoesntMatchNumberOfSelectedTasks &&
      isAssigneesListVisible
    );
  }, [
    editableTasksCountDoesntMatchNumberOfSelectedTasks,
    isAssigneesListVisible,
  ]);

  useEffect(() => {
    if (isBulkActionsBarVisible) {
      fetchTasksMetrics();
    } else {
      resetTasksMetrics();
    }
    return () => {
      resetTasksMetrics();
    };
  }, [
    isBulkActionsBarVisible,
    fetchTasksMetrics,
    resetTasksMetrics,
    numberOfSelectedTasks,
  ]);

  return useMemo(
    () => ({
      numberOrRowsSelectedLabel,
      isLoadingTasksMetrics,
      editableTasksCountDoesntMatchNumberOfSelectedTasks,
      fetchTasksMetrics,
      editableTasksCount,
      resetTasksMetrics,
      displayEditableTasksCountTooltip,
    }),
    [
      numberOrRowsSelectedLabel,
      isLoadingTasksMetrics,
      editableTasksCountDoesntMatchNumberOfSelectedTasks,
      editableTasksCount,
      fetchTasksMetrics,
      resetTasksMetrics,
      displayEditableTasksCountTooltip,
    ]
  );
};
